import { Box, Card, CardContent, Skeleton, Stack } from "@mui/material";

/**
 * Skeleton for the Performance Overview page: header, Business Health + 4 metric cards, Financial Trend + Activity Pulse.
 */
export function PerformanceOverviewSkeleton() {
  return (
    <Box
      sx={{
        px: 0,
        mt: 0,
        maxWidth: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Header: title + Export button */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={0}
        sx={{ pb: 2, mb: 0, borderBottom: "1px solid rgba(17, 24, 39, 0.08)" }}
      >
        <Skeleton variant="text" width={220} height={32} sx={{ flexShrink: 0 }} />
        <Skeleton variant="rounded" width={100} height={36} sx={{ mt: { xs: 1, sm: 0 } }} />
      </Stack>

      {/* Grid: Business Health (tall) + 4 metric cards */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "4fr 3fr 3fr" },
          gridTemplateRows: { xs: "auto", md: "auto repeat(2, auto)", lg: "repeat(2, minmax(0, 1fr))" },
          gap: 3,
          alignItems: "stretch",
        }}
      >
        <Box sx={{ gridColumn: { xs: "span 1", md: "1 / -1", lg: "1 / 2" }, gridRow: { xs: "span 1", md: "1", lg: "1 / 3" } }}>
          <Card elevation={0} sx={{ boxShadow: "none", border: "1px solid #E2E6EF", borderRadius: "12px", height: "100%", minHeight: 280 }}>
            <CardContent sx={{ p: { xs: 2, md: "24px" }, height: "100%" }}>
              <Stack spacing={2}>
                <Skeleton variant="circular" width={120} height={120} sx={{ alignSelf: "center" }} />
                <Skeleton variant="text" width="60%" sx={{ alignSelf: "center" }} />
                <Skeleton variant="text" width="40%" sx={{ alignSelf: "center" }} />
              </Stack>
            </CardContent>
          </Card>
        </Box>
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} elevation={0} sx={{ border: "1px solid #E2E6EF", borderRadius: "12px" }}>
            <CardContent sx={{ p: 2 }}>
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="text" width={80} height={36} sx={{ mt: 1 }} />
              <Skeleton variant="text" width="70%" sx={{ mt: 0.5 }} />
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Financial Trend + Activity Pulse row */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr", lg: "4fr 3fr 3fr" },
          gap: 3,
        }}
      >
        <Box sx={{ gridColumn: { xs: "span 1", lg: "1 / 3" } }}>
          <Card elevation={0} sx={{ border: "1px solid #E2E6EF", borderRadius: "12px", minHeight: 320 }}>
            <CardContent sx={{ p: { xs: 2, md: "24px" } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Skeleton variant="text" width={140} height={28} />
                <Skeleton variant="rounded" width={160} height={36} />
              </Stack>
              <Skeleton variant="rounded" width="100%" height={240} />
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ gridColumn: { xs: "span 1", lg: "3 / 4" } }}>
          <Card elevation={0} sx={{ border: "1px solid #E2E6EF", borderRadius: "12px", minHeight: 320 }}>
            <CardContent sx={{ p: { xs: 2, md: "24px" } }}>
              <Skeleton variant="text" width={120} height={24} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" width="100%" height={120} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" />
              <Skeleton variant="text" width="60%" />
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}

/**
 * Skeleton for the Performance driver (template) page: breadcrumb, title + Export, Monthly Overview, charts, detail table + vendors.
 */
export function PerformanceDriverSkeleton() {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, maxWidth: "100%", width: "100%" }}>
      {/* Breadcrumb */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Skeleton variant="text" width={160} height={24} />
        <Skeleton variant="text" width={12} height={24} />
        <Skeleton variant="text" width={100} height={24} />
      </Stack>

      {/* Title + Export */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2.5} sx={{ flexWrap: "wrap" }}>
        <Skeleton variant="text" width={220} height={36} />
        <Skeleton variant="rounded" width={100} height={36} />
      </Stack>

      <Box sx={{ borderBottom: "1px solid #E5E7EB" }}>
        <Skeleton variant="rounded" height={1} sx={{ opacity: 0 }} />
      </Box>

      {/* Monthly Overview card with 3 KPIs */}
      <Card sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", p: "20px" }}>
        <Stack spacing={2}>
          <Skeleton variant="text" width={180} height={28} />
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 2.5 }}>
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ p: 2, bgcolor: "rgba(0,0,0,0.02)", borderRadius: "10px" }}>
                <Skeleton variant="text" width="70%" />
                <Skeleton variant="text" width={100} height={32} sx={{ mt: 1 }} />
                <Skeleton variant="text" width="50%" sx={{ mt: 0.5 }} />
              </Box>
            ))}
          </Box>
        </Stack>
      </Card>

      {/* Two cards: Monthly Breakdown + Category Spend */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2.5 }}>
        <Card sx={{ border: "1px solid #E5E7EF", borderRadius: "12px", p: "20px", minHeight: 360 }}>
          <Skeleton variant="text" width={180} height={28} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" width="100%" height={300} />
        </Card>
        <Card sx={{ border: "1px solid #E5E7EF", borderRadius: "12px", p: "20px", minHeight: 360 }}>
          <Skeleton variant="text" width={200} height={28} sx={{ mb: 2 }} />
          <Skeleton variant="circular" width={200} height={200} sx={{ mx: "auto", display: "block" }} />
        </Card>
      </Box>

      {/* Two cards: Detailed Breakdown + Top 5 Vendors */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" }, gap: 2.5 }}>
        <Card sx={{ border: "1px solid #E5E7EF", borderRadius: "12px", p: "20px", minHeight: 320 }}>
          <Skeleton variant="text" width={180} height={28} sx={{ mb: 2 }} />
          <Skeleton variant="rounded" width="100%" height={40} sx={{ mb: 1 }} />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="text" width="100%" height={32} sx={{ my: 0.5 }} />
          ))}
        </Card>
        <Card sx={{ border: "1px solid #E5E7EF", borderRadius: "12px", p: "20px", minHeight: 320 }}>
          <Skeleton variant="text" width={140} height={28} sx={{ mb: 2 }} />
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} variant="text" width="100%" height={28} sx={{ my: 0.5 }} />
          ))}
        </Card>
      </Box>
    </Box>
  );
}
